import type { QueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/auth-store';
import { sessionRevision, sessionScope, sessionTenantId } from '@/features/auth/session-scope';
import { notificationUUID, readNotificationRow } from './contract';

export const notificationRootKey = () => ['saved-notifications',sessionScope(useAuthStore.getState()),sessionRevision()] as const;
let ledgerOwner = '';
const seen = new Set<string>();
const reads=new Map<string,{dirty:number;promise:Promise<void>}>();
export function reconcileNotifications(client: QueryClient): Promise<void> {
  const s=useAuthStore.getState();
  if (!s.token || !notificationUUID(s.user?.id) || !notificationUUID(sessionTenantId(s))) return Promise.resolve();
  const key=notificationRootKey(),owner=JSON.stringify(key),revision=sessionRevision(),running=reads.get(owner);
  if(running){running.dirty++;return running.promise;}
  const state={dirty:1,promise:Promise.resolve()};reads.set(owner,state);
  state.promise=(async()=>{
    try{
      while(revision===sessionRevision()){
        const target=state.dirty;
        await client.cancelQueries({queryKey:key});
        if(revision!==sessionRevision())return;
        await client.invalidateQueries({queryKey:key});
        if(target===state.dirty)return;
      }
    }finally{reads.delete(owner);}
  })();
  return state.promise;
}
/** Native owned hints only. No invented unread count, alert, write or locale change. */
export function receiveNotificationHint(client: QueryClient,type:string,value:unknown): boolean {
  if(!['notification:new','notification:read','notification:deleted'].includes(type))return false;
  const s=useAuthStore.getState(), owner={user:s.user?.id,tenant:sessionTenantId(s)};
  if (!s.token || !notificationUUID(owner.user) || !notificationUUID(owner.tenant)) return false;
  let id:string;
  try {
    if (type==='notification:new') id=readNotificationRow(value,owner).id;
    else {
      if (!value || typeof value!=='object' || Array.isArray(value)) return false;
      const v=value as Record<string,unknown>;
      if (v.userId!==owner.user || v.tenantId!==owner.tenant || !notificationUUID(v.eventId) || (v.all===true ? v.id!==undefined : !notificationUUID(v.id))) return false;
      id=v.eventId;
    }
  } catch { return false; }
  const scope=JSON.stringify(notificationRootKey());
  if (ledgerOwner!==scope) { ledgerOwner=scope;seen.clear(); }
  const key=type+':'+id;
  if (seen.has(key)) return false;
  seen.add(key);if(seen.size>250)seen.delete(seen.values().next().value!);
  void reconcileNotifications(client);
  return true;
}
